import React, { useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";
import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Toaster } from "react-hot-toast";
import { DataGrid } from "@mui/x-data-grid";
import Chart from "react-apexcharts";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import exportExcel from "utils/excelGenerate";
import { getTaskStats, getVisitsStats } from "_controllers/officer";
import { toastMessage } from "utils/toastMessage";
import {
  PageHeader,
  StatCard,
  SectionCard,
  EmptyState,
  useChartBase,
  useChartPalette,
  mergeChartOptions,
} from "_shared/ui";

const Main = () => {
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [taskData, setTaskData] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [loading, setLoading] = useState(true);

  const columns = useMemo(() => [
    {
      field: "SAP_ID",
      headerName: "SAP ID",
      flex: 1,
    },
    {
      field: "zone",
      headerName: "Zone",
      flex: 1,
    },
    {
      field: "region",
      headerName: "Region",
      flex: 2,
    },
    {
      field: "area",
      headerName: "Area",
      flex: 2,
    },
    {
      field: "Completed",
      headerName: "Completed",
      flex: 1,
    },
    {
      field: "WIP",
      headerName: "WIP",
      flex: 1,
    },
    {
      field: "Pending",
      headerName: "Pending",
      flex: 1,
    },
  ]);

  const handleChange = (e) => {
    setSearch(e.target.value);
  };

  useEffect(() => {
    getTaskStats()
      .then((d) => {
        console.log("Task Stats API Response:", d);

        let parsed = d;
        if (typeof d === "string") {
          try { parsed = JSON.parse(d); } catch (e) {}
        }

        let arrayData = [];
        if (Array.isArray(parsed)) arrayData = parsed;
        else if (Array.isArray(parsed?.taskStats)) arrayData = parsed.taskStats;
        else if (Array.isArray(parsed?.data?.taskStats)) arrayData = parsed.data.taskStats;
        else if (Array.isArray(parsed?.data)) arrayData = parsed.data;

        const test = arrayData.map((user) => ({
          id: v4(),
          SAP_ID: user.SAP_ID,
          zone: user.zone ? user.zone : "-",
          region: user.regionName ? user.regionName : "-",
          area: user.areaName ? user.areaName : "-",
          territory: user.territoryName ? user.territoryName : "-",
          Completed: user?.stats?.Completed || 0,
          WIP: user?.stats?.WIP || 0,
          Pending: user?.stats?.["To Be Started"] || 0,
        }));
        setTaskData(test);
        setLoading(false);
      })
      .catch((err) => {
        console.log("err", err);
        setLoading(false);
        toastMessage("error fetching data", "error");
      });
  }, []);

  useEffect(() => {
    const res = taskData
      .filter(
        (data) =>
          data?.SAP_ID?.toUpperCase().includes(search.toUpperCase()) ||
          data?.zone?.toUpperCase().includes(search.toUpperCase()) ||
          data?.region?.toUpperCase().includes(search.toUpperCase()) ||
          data?.area?.toUpperCase().includes(search.toUpperCase())
      )
      .map((user) => ({
        id: user?.id,
        SAP_ID: user.SAP_ID,
        zone: user.zone ? user.zone : "-",
        region: user.region ? user.region : "-",
        area: user.area ? user.area : "-",
        territory: user.territory ? user.territory : "-",
        Completed: user?.Completed || 0,
        WIP: user?.WIP || 0,
        Pending: user?.Pending || 0,
      }));
    setSearchData(res);
  }, [search, taskData]);

  let fileName = "Task-Stats";

  const generateExcel = async (e) => {
    e.preventDefault();
    exportExcel(
      taskData.map((x) => ({
        "SAP ID": x?.SAP_ID,
        Zone: x?.zone,
        Region: x?.region,
        Area: x?.area,
        Completed: x?.Completed,
        WIP: x?.WIP,
        Pending: x?.Pending,
      })),
      fileName
    );
  };

  // Aggregate task totals across every representative (derived from the data
  // already loaded — no new API calls or fabricated metrics).
  const totals = useMemo(
    () =>
      taskData.reduce(
        (acc, r) => {
          acc.completed += Number(r.Completed) || 0;
          acc.wip += Number(r.WIP) || 0;
          acc.pending += Number(r.Pending) || 0;
          return acc;
        },
        { completed: 0, wip: 0, pending: 0 }
      ),
    [taskData]
  );
  const totalTasks = totals.completed + totals.wip + totals.pending;

  // Theme-aware ApexCharts options (light + dark) via the shared adapter.
  const chartBase = useChartBase();
  const chartPalette = useChartPalette();

  const statusOptions = mergeChartOptions(chartBase, {
    labels: ["Completed", "WIP", "Pending"],
    colors: [
      chartPalette[2], // success
      chartPalette[1], // info
      chartPalette[3], // warning
    ],
    legend: { position: "bottom" },
    dataLabels: { enabled: true },
    plotOptions: { pie: { donut: { size: "68%" } } },
  });
  const statusSeries = [totals.completed, totals.wip, totals.pending];

  const statusBarOptions = mergeChartOptions(chartBase, {
    chart: { type: "bar" },
    colors: [chartPalette[2], chartPalette[1], chartPalette[3]],
    plotOptions: {
      bar: { horizontal: true, distributed: true, borderRadius: 0 },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: { categories: ["Completed", "WIP", "Pending"] },
  });
  const statusBarSeries = [
    { name: "Tasks", data: [totals.completed, totals.wip, totals.pending] },
  ];

  return (
    <>
      <PageHeader
        title="Task Stats"
        subtitle="Task completion status by representative"
        action={
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={generateExcel}
          >
            Download
          </Button>
        }
      />

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Field Reps"
                value={taskData.length}
                icon={PeopleAltOutlinedIcon}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Completed"
                value={totals.completed}
                icon={CheckCircleOutlineIcon}
                color="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Work In Progress"
                value={totals.wip}
                icon={AutorenewIcon}
                color="info"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Pending"
                value={totals.pending}
                icon={PendingActionsOutlinedIcon}
                color="warning"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} md={5}>
              <SectionCard title="Task Status Distribution">
                {totalTasks > 0 ? (
                  <Chart
                    type="donut"
                    series={statusSeries}
                    options={statusOptions}
                    height={320}
                  />
                ) : (
                  <EmptyState
                    title="No tasks recorded"
                    description="Task status distribution will appear once data is available."
                  />
                )}
              </SectionCard>
            </Grid>
            <Grid item xs={12} md={7}>
              <SectionCard title="Tasks by Status">
                <Chart
                  type="bar"
                  series={statusBarSeries}
                  options={statusBarOptions}
                  height={320}
                />
              </SectionCard>
            </Grid>
          </Grid>

          <SectionCard title="Representative Stats">
            <Box sx={{ mb: 2 }}>
              <TextField
                size="small"
                onChange={handleChange}
                value={search}
                name="search"
                label="SAP ID or Territory"
                sx={{ width: { xs: "100%", sm: 360 } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <DataGrid
              rows={search ? searchData : taskData}
              columns={columns}
              autoHeight={true}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              className="TableCell"
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ fontSize: "0.75rem" }}
            />
          </SectionCard>
        </>
      )}
      <Toaster />
    </>
  );
};

const TaskStats = () => {
  return (
    <AccessController roles={["AO", "RO", "ZO", "ADMIN"]}>
      <DashGridL2 Top={TopBar} Main={Main} />
      <Toaster />
    </AccessController>
  );
};

export default TaskStats;
